import React, { useState } from 'react';
import { useNavigate, Navigate, useLocation } from 'react-router-dom';
import { FaEnvelope, FaLock, FaSpinner } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, isParceiro, isAdmin } = useAuth();

  // Rota de origem passada pelo PrivateRoute/AdminRoute via state
  const from = (location.state as { from?: string })?.from;

  // Se já está autenticado, redireciona para a área apropriada
  if (isAuthenticated) {
    if (from) {
      return <Navigate to={from} replace />;
    }
    if (isAdmin) {
      return <Navigate to="/admin/postos" replace />;
    }
    if (isParceiro) {
      return <Navigate to="/postos" replace />;
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error('Por favor, preencha todos os campos');
      return;
    }

    setIsLoading(true);

    try {
      await login(email, password);
      toast.success('Login realizado com sucesso!');

      // Se havia uma rota de destino salva, redirecionar para ela
      if (from) {
        navigate(from, { replace: true });
        return;
      }

      // Redirecionar baseado no nível de acesso
      const userData = localStorage.getItem('user');
      if (userData) {
        const parsedUser = JSON.parse(userData);
        if (parsedUser.nivelAcesso === 'ADMIN') {
          navigate('/admin/postos');
        } else {
          navigate('/postos');
        }
      } else {
        navigate('/postos');
      }
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'Erro ao fazer login';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="h-screen flex items-center justify-center bg-gradient-to-br from-[#0f1419] via-[#1a2332] to-[#253547] relative overflow-hidden p-5">
      <div className="absolute top-0 left-0 right-0 bottom-0 overflow-hidden">
        <div className="absolute w-[600px] h-[600px] -top-[200px] -right-[200px] rounded-full bg-gradient-to-br from-[#089453]/10 to-[#84CC34]/5 animate-[float_20s_infinite_ease-in-out]"></div>
        <div className="absolute w-[400px] h-[400px] -bottom-[100px] -left-[100px] rounded-full bg-gradient-to-br from-[#089453]/10 to-[#84CC34]/5 animate-[float_25s_infinite_ease-in-out_reverse]"></div>
        <div className="absolute w-[300px] h-[300px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-br from-[#089453]/10 to-[#84CC34]/5 animate-[pulse-bg_10s_infinite_ease-in-out]"></div>
      </div>

      <div className="bg-[#1a2332]/95 border border-white/10 rounded-3xl p-8 w-full max-w-[420px] max-h-[calc(100vh-40px)] overflow-y-auto shadow-[0_25px_50px_rgba(0,0,0,0.5)] relative z-10 backdrop-blur-[10px] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-white/5 [&::-webkit-scrollbar-track]:rounded [&::-webkit-scrollbar-thumb]:bg-[#84CC34]/50 [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb:hover]:bg-[#84CC34]/70">
        <div className="text-center mb-6">
          <div className="w-20 h-20 rounded-[20px] flex items-center justify-center mx-auto mb-4 overflow-hidden">
            <img src="/logo.png" alt="AbasteceMais" className="w-full h-full object-contain" />
          </div>
          <h1 className="text-[28px] font-bold text-white m-0 mb-2">AbasteceMais</h1>
          <p className="text-[#888] text-sm m-0">Painel do Posto Parceiro</p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          <div className="flex flex-col gap-2">
            <label htmlFor="email" className="text-[#aaa] text-sm font-medium">E-mail</label>
            <div className="relative flex items-center">
              <FaEnvelope className="absolute left-4 text-[#666] text-lg transition-colors duration-200 peer-focus:text-[#089453]" />
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                disabled={isLoading}
                className="peer w-full py-4 pl-[50px] pr-4 bg-white/5 border-2 border-white/10 rounded-xl text-white text-base transition-all duration-200 placeholder:text-[#666] focus:outline-none focus:border-[#089453] focus:bg-[#089453]/5 disabled:opacity-70 disabled:cursor-not-allowed"
              />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="password" className="text-[#aaa] text-sm font-medium">Senha</label>
            <div className="relative flex items-center">
              <FaLock className="absolute left-4 text-[#666] text-lg transition-colors duration-200 peer-focus:text-[#089453]" />
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                disabled={isLoading}
                className="peer w-full py-4 pl-[50px] pr-4 bg-white/5 border-2 border-white/10 rounded-xl text-white text-base transition-all duration-200 placeholder:text-[#666] focus:outline-none focus:border-[#089453] focus:bg-[#089453]/5 disabled:opacity-70 disabled:cursor-not-allowed"
              />
            </div>
          </div>

          <button
            type="submit"
            className="bg-gradient-to-br from-[#089453] to-[#84CC34] text-white py-4 px-4 border-none rounded-xl text-base font-semibold cursor-pointer transition-all duration-300 flex items-center justify-center gap-2.5 mt-2 hover:enabled:-translate-y-0.5 hover:enabled:shadow-[0_10px_30px_rgba(8,148,83,0.4)] disabled:opacity-70 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <FaSpinner className="animate-spin" />
                Entrando...
              </>
            ) : (
              'Entrar'
            )}
          </button>
        </form>

        <div className="text-center mt-6 pt-5 border-t border-white/10">
          <p className="text-[#666] text-[13px] m-0 mb-3">Acesso exclusivo para postos parceiros</p>
          <p className="text-[#666] text-[13px] m-0 mt-4">
            Ainda não é parceiro?{' '}
            <button
              onClick={() => navigate('/cadastro-parceiro')}
              className="bg-transparent border-none text-[#84cc34] font-semibold cursor-pointer transition-colors duration-200 p-0 text-[13px] hover:enabled:text-[#089453] hover:enabled:underline disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading}
            >
              Cadastre-se aqui
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;

